import React from 'react';
import { motion } from 'framer-motion';
import { ConsentDetails, formatConsentId, formatRelativeTime, isExpiringSoon } from '../../constants/uaeStandards';
import { ConsentStateBadge } from './ConsentStateBadge';
import { AlertTriangleIcon, ChevronRightIcon, CalendarIcon, ActivityIcon } from 'lucide-react';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { getShortBankName } from '../../constants/bankThemes';
interface ConsentCardProps {
  consent: ConsentDetails;
  onClick: () => void;
  context: 'TPP' | 'LFI';
}
export function ConsentCard({
  consent,
  onClick,
  context
}: ConsentCardProps) {
  const showExpiryWarning = isExpiringSoon(consent.expiryDate);
  const isTPP = context === 'TPP';
  // Use short bank name for display
  const displayName = isTPP ? getShortBankName(consent.bankName) : consent.tppTradingName;
  return <motion.button onClick={onClick} whileHover={{
    scale: 1.01,
    y: -2
  }} whileTap={{
    scale: 0.99
  }} className="w-full text-left p-4 bg-white border-2 transition-all" style={{
    borderColor: OPENFINANCE_THEME.colors.border,
    borderRadius: OPENFINANCE_THEME.radius.lg,
    boxShadow: OPENFINANCE_THEME.shadows.sm
  }} data-id="element-1590">
      {/* Header: Logo + Name + State */}
      <div className="flex items-start justify-between mb-3" data-id="element-1591">
        <div className="flex items-center gap-3 flex-1" data-id="element-1592">
          <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center p-2 bg-gray-50" style={{
          borderRadius: OPENFINANCE_THEME.radius.md
        }} data-id="element-1593">
            <img src={isTPP ? consent.bankLogo : consent.tppLogo} alt={isTPP ? consent.bankName : consent.tppName} className="w-full h-full object-contain" data-id="element-1594" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1595">
            <div className="font-semibold text-base truncate" style={{
            color: OPENFINANCE_THEME.colors.text,
            fontFamily: OPENFINANCE_THEME.fonts.body
          }} data-id="element-1596">
              {displayName}
            </div>
            <div className="text-xs mt-0.5" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1597">
              {consent.merchantName && `${consent.merchantName} via `}
              {consent.type === 'DataSharing' ? 'Data Sharing' : 'Service Initiation'}
            </div>
          </div>
        </div>
        <ConsentStateBadge state={consent.state} size="sm" data-id="element-1598" />
      </div>

      {/* Account Info */}
      <div className="text-sm mb-3 flex items-center gap-2" style={{
      color: OPENFINANCE_THEME.colors.textLight
    }} data-id="element-1599">
        <span className="font-medium" data-id="element-1600">{consent.accountType}</span>
        <span className="text-xs" data-id="element-1601">•</span>
        <span className="font-mono text-xs" style={{
        color: OPENFINANCE_THEME.colors.textMuted
      }} data-id="element-1602">
          ****{consent.accountLastFour}
        </span>
      </div>

      {/* Dates */}
      <div className="space-y-2 mb-3" data-id="element-1603">
        <div className="flex items-center gap-2 text-xs" data-id="element-1604">
          <CalendarIcon className="w-4 h-4 flex-shrink-0" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1605" />
          <span style={{
          color: OPENFINANCE_THEME.colors.textLight
        }} data-id="element-1606">
            Expires: <span className="font-medium" data-id="element-1607">{consent.expiryDate}</span>
          </span>
        </div>
        {consent.lastAccessDate && <div className="flex items-center gap-2 text-xs" data-id="element-1608">
            <ActivityIcon className="w-4 h-4 flex-shrink-0" style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1609" />
            <span style={{
          color: OPENFINANCE_THEME.colors.textLight
        }} data-id="element-1610">
              Last access:{' '}
              <span className="font-medium" data-id="element-1611">
                {formatRelativeTime(consent.lastAccessDate)}
              </span>
            </span>
          </div>}
      </div>

      {/* Expiry Warning */}
      {showExpiryWarning && consent.state === 'Authorized' && <div className="flex items-center gap-2 p-2 mb-3 text-xs rounded-lg" style={{
      backgroundColor: OPENFINANCE_THEME.colors.accent300 + '20',
      border: `1px solid ${OPENFINANCE_THEME.colors.accent500}`
    }} data-id="element-1612">
          <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" style={{
        color: OPENFINANCE_THEME.colors.accent600
      }} data-id="element-1613" />
          <span style={{
        color: OPENFINANCE_THEME.colors.accent600
      }} data-id="element-1614">
            Expiring soon - consider renewing
          </span>
        </div>}

      {/* Footer: Consent ID + Arrow */}
      <div className="flex items-center justify-between pt-3 border-t" style={{
      borderColor: OPENFINANCE_THEME.colors.borderLight
    }} data-id="element-1615">
        <div className="text-xs" data-id="element-1616">
          <span style={{
          color: OPENFINANCE_THEME.colors.textMuted
        }} data-id="element-1617">
            ID:{' '}
          </span>
          <span className="font-mono font-medium" style={{
          color: OPENFINANCE_THEME.colors.textLight
        }} data-id="element-1618">
            {formatConsentId(consent.consentId)}
          </span>
        </div>
        <ChevronRightIcon className="w-5 h-5" style={{
        color: OPENFINANCE_THEME.colors.textMuted
      }} data-id="element-1619" />
      </div>
    </motion.button>;
}